import React, { useState, useEffect, useRef, useCallback, useMemo, useContext } from 'react';
import {
  area,
  length,
  polygon as turfPolygon,
  lineString
} from '@turf/turf';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { getData, postData, putData, deleteData } from '../../../../services/Fetchnodeservices';
import { ThemeContext } from '../../../../context/ThemeContext';

// Fix Leaflet default markers for webpack/bundler environments
if (typeof window !== 'undefined') {
  delete L.Icon.Default.prototype._getIconUrl;
  L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  });
}

// ─── Constants ──────────────────────────────────────────────────────
const DEFAULT_GWALIOR_CENTER = [26.208345, 78.172796];
const DEFAULT_GWALIOR_ZOOM = 13;

// OpenStreetMap tile layers (free, no API key)
const TILE_LAYERS = {
  street: {
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    options: {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19,
    }
  },
  satellite: {
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    options: {
      attribution: '&copy; Esri, Maxar, Earthstar Geographics',
      maxZoom: 18,
    }
  }
};

// ─── Helpers ────────────────────────────────────────────────────────
function coordinatePair(value) {
  if (!Array.isArray(value) || value.length < 2) return null;
  const first = Number(value[0]);
  const second = Number(value[1]);
  if (!Number.isFinite(first) || !Number.isFinite(second)) return null;
  return { lat: first, lng: second };
}

function placeCoordinates(place) {
  if (!place) return null;
  if (Array.isArray(place)) return coordinatePair(place);
  const lat = Number(place?.latitude ?? place?.lat ?? place?.Latitude ?? place?.Lat ?? place?.y);
  const lng = Number(place?.longitude ?? place?.lng ?? place?.lon ?? place?.Longitude ?? place?.Lng ?? place?.Lon ?? place?.x);
  return (Number.isFinite(lat) && Number.isFinite(lng) && lat >= 6 && lat <= 37 && lng >= 68 && lng <= 98) ? { lat, lng } : null;
}

function distanceKm(from, to) {
  const lat1 = Number(from?.lat), lng1 = Number(from?.lng), lat2 = Number(to?.lat), lng2 = Number(to?.lng);
  if (![lat1, lng1, lat2, lng2].every(Number.isFinite)) return null;
  const R = 6371, dLat = (lat2 - lat1) * Math.PI / 180, dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function circleToPolygonPoints(center, radiusKm, steps = 40) {
  const points = [];
  const R = 6371, d = radiusKm / R;
  const lat1 = center.lat * Math.PI / 180, lng1 = center.lng * Math.PI / 180;
  for (let i = 0; i <= steps; i++) {
    const brng = (i / steps) * 2 * Math.PI;
    const lat2 = Math.asin(Math.sin(lat1) * Math.cos(d) + Math.cos(lat1) * Math.sin(d) * Math.cos(brng));
    const lng2 = lng1 + Math.atan2(Math.sin(brng) * Math.sin(d) * Math.cos(lat1), Math.cos(d) - Math.sin(lat1) * Math.sin(lat2));
    points.push({ lat: lat2 * 180 / Math.PI, lng: lng2 * 180 / Math.PI });
  }
  return points;
}

function getPolygonCentroid(coords) {
  if (!coords || coords.length < 3) return null;
  let lat = 0, lng = 0;
  for (const p of coords) { lat += p.lat; lng += p.lng; }
  return { lat: lat / coords.length, lng: lng / coords.length };
}

function mapEventPoint(event) {
  const ll = event?.latlng;
  if (!ll) return null;
  const lat = Number(ll.lat), lng = Number(ll.lng);
  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
}

// Extract {lat, lng} array from a Leaflet polygon (supports Polygon & MultiPolygon)
function polyToCoords(layer) {
  if (!layer || typeof layer.getLatLngs !== 'function') return [];
  try {
    const latlngs = layer.getLatLngs();
    if (Array.isArray(latlngs) && latlngs.length && Array.isArray(latlngs[0][0])) {
      return latlngs[0][0].map(ll => ({ lat: Number(ll.lat), lng: Number(ll.lng) }));
    }
    if (Array.isArray(latlngs) && latlngs.length && typeof latlngs[0].lat === 'number') {
      return latlngs.map(ll => ({ lat: Number(ll.lat), lng: Number(ll.lng) }));
    }
    return [];
  } catch (_) {
    return [];
  }
}

// ─── Reverse Geocoding via Backend (Nominatim) ──────────────────────
async function getAddressFromCoords(lat, lng) {
  try {
    const data = await getData(`admin/settings/regions/reverse?lat=${encodeURIComponent(lat)}&lng=${encodeURIComponent(lng)}`);
    if (data?.status && data?.data?.display_name) return data.data.display_name;
    if (data && data.address) {
      const addr = data.address;
      const parts = [addr.neighbourhood || addr.suburb, addr.city || addr.town || addr.village, addr.state, addr.country].filter(Boolean);
      return parts.join(', ') || data.display_name || 'Unknown location';
    }
    return 'Unknown location';
  } catch {
    return 'Unknown location';
  }
}

// ─── City/Area Search via Backend ────────────────────────────────────
async function searchLocation(query) {
  if (!query || query.length < 2) return [];
  try {
    const backendResponse = await getData(`admin/settings/regions/search?q=${encodeURIComponent(query)}&limit=8`);
    const data = backendResponse?.status ? backendResponse.data : [];
    return data.map(item => {
      let polygon = null;
      if (item.geojson && (item.geojson.type === 'Polygon' || item.geojson.type === 'MultiPolygon')) {
        const coords = item.geojson.type === 'MultiPolygon' ? item.geojson.coordinates[0][0] : item.geojson.coordinates[0];
        polygon = coords.map(coord => ({ lat: Number(coord[1]), lng: Number(coord[0]) }));
        if (polygon.length > 2500) {
          const step = Math.ceil(polygon.length / 1800);
          const simplified = polygon.filter((_, i) => i % step === 0);
          const last = polygon[polygon.length - 1];
          const lastSimp = simplified[simplified.length - 1];
          if (last && lastSimp && (last.lat !== lastSimp.lat || last.lng !== lastSimp.lng)) simplified.push(last);
          polygon = simplified;
        }
      }
      return {
        name: item.display_name?.split(',')[0] || 'Unknown',
        lat: parseFloat(item.lat),
        lng: parseFloat(item.lon),
        display: item.display_name,
        displayName: item.display_name,
        bbox: item.boundingbox || null,
        polygon,
        place_id: item.place_id,
        source: item.source || 'OpenStreetMap',
        mappls_pin: null
      };
    });
  } catch {
    return [];
  }
}

async function searchLocationBackend(query, limit = 8) {
  if (!query || query.length < 2) return [];
  try {
    const response = await getData(`admin/settings/regions/search?q=${encodeURIComponent(query)}&limit=${limit}`);
    if (!response?.status || !Array.isArray(response.data)) return [];
    return response.data.map(item => {
      let polygon = null;
      if (item.geojson && (item.geojson.type === 'Polygon' || item.geojson.type === 'MultiPolygon')) {
        const coords = item.geojson.type === 'MultiPolygon' ? item.geojson.coordinates[0][0] : item.geojson.coordinates[0];
        polygon = coords.map(coord => ({ lat: Number(coord[1]), lng: Number(coord[0]) }));
        if (polygon.length > 2500) {
          const step = Math.ceil(polygon.length / 1800);
          const simplified = polygon.filter((_, i) => i % step === 0);
          const last = polygon[polygon.length - 1];
          const lastSimp = simplified[simplified.length - 1];
          if (last && lastSimp && (last.lat !== lastSimp.lat || last.lng !== lastSimp.lng)) simplified.push(last);
          polygon = simplified;
        }
      }
      return {
        name: (item.display_name || '').split(',')[0] || item.name || 'Unknown',
        lat: Number(item.lat),
        lng: Number(item.lon || item.lng),
        display: item.display_name,
        displayName: item.display_name,
        bbox: item.boundingbox || null,
        polygon,
        place_id: item.place_id,
        source: item.source || null,
        mappls_pin: null,
        boundary_scope: item.boundary_scope || null,
        ward_number: item.ward_number || null,
        ward_areas: Array.isArray(item.ward_areas) ? item.ward_areas : [],
        matched_areas: Array.isArray(item.matched_areas) ? item.matched_areas : [],
      };
    });
  } catch (error) {
    console.warn('Backend city search fallback failed:', error);
    return [];
  }
}

// ─── Main Component ──────────────────────────────────────────────────
export default function ZoneManagement() {
  const { theme: globalTheme } = useContext(ThemeContext);

  const theme = useMemo(() => ({
    bg: globalTheme.background,
    surface: globalTheme.surface,
    surfaceHover: globalTheme.surfaceHover || `${globalTheme.primary}08`,
    border: globalTheme.border,
    text: globalTheme.textPrimary,
    textSecondary: globalTheme.textSecondary,
    textMuted: globalTheme.textMuted,
    primary: globalTheme.primary,
    primaryLight: `${globalTheme.primary}15`,
    success: globalTheme.success,
    successLight: `${globalTheme.success}15`,
    danger: globalTheme.error || globalTheme.danger,
    dangerLight: `${globalTheme.error || globalTheme.danger}15`,
    warning: globalTheme.warning,
    warningLight: `${globalTheme.warning}15`,
  }), [globalTheme]);

  // ─── State ──────────────────────────────────────────────────────────
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [mapsReady, setMapsReady] = useState(false);
  const [mapLoadError, setMapLoadError] = useState('');
  const [mapRetryVersion, setMapRetryVersion] = useState(0);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const tileLayerRef = useRef(null);
  const citySearchInputRef = useRef(null);
  const citySearchQueryRef = useRef('');
  const areaSearchInputRef = useRef(null);
  const citySearchBoundRef = useRef(false);
  const areaSearchBoundRef = useRef(false);
  const selectCityRef = useRef(null);
  const previewAreaRefCallback = useRef(null);
  const polygonsRef = useRef(new Map());
  const drawingPoly = useRef(null);
  const editablePoly = useRef(null);
  const manualDrawListener = useRef(null);

  const [selectedAreas, setSelectedAreas] = useState([]);
  const selectedAreaLayers = useRef(new Map());

  const [mode, setMode] = useState('view');
  const [editId, setEditId] = useState(null);
  const [formName, setFormName] = useState('');
  const [formType, setFormType] = useState('allowed');
  const [blockingAreaName, setBlockingAreaName] = useState('');
  const [drawnCoords, setDrawnCoords] = useState([]);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [areaInfo, setAreaInfo] = useState({ area: null, perimeter: null, points: 0 });

  const [mapStyle, setMapStyle] = useState('street');
  const [removePointMode, setRemovePointMode] = useState(false);

  const [selectedCity, setSelectedCity] = useState(null);

  const [areaQuery, setAreaQuery] = useState('');
  const [areaResults, setAreaResults] = useState([]);
  const [areaSearching, setAreaSearching] = useState(false);
  const [showAreaResults, setShowAreaResults] = useState(false);
  const areaSearchTimer = useRef(null);
  const areaSearchRequestId = useRef(0);

  const areaPreviewRef = useRef(null);
  const cityBoundaryRef = useRef(null);
  const testMarkerRef = useRef(null);
  const [testingLocation, setTestingLocation] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const skipCityInputHandler = useRef(false);
  const cityDebounceRef = useRef(null);
  const skipAreaInputHandler = useRef(false);

  // ─── API calls ──────────────────────────────────────────────────────
  const loadZones = useCallback(async () => {
    try {
      const res = await getData('admin/settings/regions');
      if (res.error) {
        console.error('API error:', res.error);
        setMsg({ text: 'Error loading zones: ' + res.error, type: 'error' });
        setZones([]);
      } else if (res.status) {
        setZones(res.data);
      } else {
        setMsg({ text: res.message || 'Failed to load zones', type: 'error' });
        setZones([]);
      }
    } catch (err) {
      console.error('Exception:', err);
      setMsg({ text: 'Error loading zones', type: 'error' });
      setZones([]);
    }
  }, []);

  const createZone = async (data) => {
    const res = await postData('admin/settings/regions', data);
    if (res.error) throw new Error(res.error);
    return res;
  };

  const updateZone = async (id, data) => {
    const res = await putData(`admin/settings/regions/${id}`, data);
    if (res.error) throw new Error(res.error);
    return res;
  };

  const deleteZoneApi = async (id) => {
    const res = await deleteData(`admin/settings/regions/${id}`);
    if (res.error) throw new Error(res.error);
    return res;
  };

  useEffect(() => {
    setLoading(true);
    loadZones().finally(() => setLoading(false));
  }, [loadZones]);

  const retryMap = useCallback(() => {
    try { mapInstance.current?.remove?.(); } catch (_) {}
    mapInstance.current = null;
    tileLayerRef.current = null;
    citySearchBoundRef.current = false;
    areaSearchBoundRef.current = false;
    setMapsReady(false);
    setMapLoadError('');
    setMapRetryVersion(v => v + 1);
  }, []);

  // ─── Initialize Leaflet Map ────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    let mapLoadTimer = null;

    const retryWhenBackOnline = () => {
      if (!mapInstance.current) retryMap();
    };

    function createMap() {
      if (cancelled || !mapRef.current || mapInstance.current) return;
      if (mapLoadTimer) clearTimeout(mapLoadTimer);

      try {
        const map = L.map(mapRef.current, {
          center: DEFAULT_GWALIOR_CENTER,
          zoom: DEFAULT_GWALIOR_ZOOM,
          zoomControl: true,
          scrollWheelZoom: true,
        });

        // Add OSM tile layer
        const tileConfig = TILE_LAYERS[mapStyle] || TILE_LAYERS.street;
        tileLayerRef.current = L.tileLayer(tileConfig.url, tileConfig.options).addTo(map);

        mapInstance.current = map;

        // Init Leaflet.PolyDraw plugin
        if (typeof map.pm !== 'undefined' && map.pm.addControls) {
          map.pm.addControls({
            position: 'topleft',
            drawControls: false,
            editControls: false,
          });
        }

        if (!cancelled) {
          setMapLoadError('');
          setMapsReady(true);
        }
      } catch (error) {
        console.error('Leaflet map creation failed:', error);
        if (!cancelled) setMapLoadError('Map could not be created. Please press Retry.');
      }
    }

    // Small delay to ensure DOM is ready
    mapLoadTimer = window.setTimeout(createMap, 100);

    window.addEventListener('online', retryWhenBackOnline);
    mapLoadTimer = window.setTimeout(() => {
      if (!cancelled && !mapInstance.current) {
        setMapLoadError('Map could not load. Check internet connection, then press Retry.');
      }
    }, 15000);

    return () => {
      cancelled = true;
      window.removeEventListener('online', retryWhenBackOnline);
      if (mapLoadTimer) clearTimeout(mapLoadTimer);
      try { mapInstance.current?.remove?.(); } catch (_) {}
      mapInstance.current = null;
      tileLayerRef.current = null;
      citySearchBoundRef.current = false;
      areaSearchBoundRef.current = false;
    };
  }, [mapRetryVersion, mapStyle, retryMap]);

  // ─── Switch tile layer ──────────────────────────────────────────────
  useEffect(() => {
    const map = mapInstance.current;
    if (!map) return;

    if (tileLayerRef.current) {
      try { map.removeLayer(tileLayerRef.current); } catch (_) {}
    }

    const tileConfig = TILE_LAYERS[mapStyle] || TILE_LAYERS.street;
    tileLayerRef.current = L.tileLayer(tileConfig.url, tileConfig.options).addTo(map);

    polygonsRef.current.forEach(layer => { try { layer.bringToFront(); } catch {} });
    try { drawingPoly.current?.bringToFront(); } catch {}
    try { editablePoly.current?.bringToFront(); } catch {}
  }, [mapStyle, mapsReady]);

  // ─── Render zones on map ──────────────────────────────────────────
  useEffect(() => {
    if (!mapInstance.current || !mapsReady) return;
    polygonsRef.current.forEach(p => { try { p.remove(); } catch {} });
    polygonsRef.current.clear();

    const filtered = zones.filter(Boolean).filter(z => {
      if (filter === 'allowed' && z.zone_type !== 'allowed') return false;
      if (filter === 'blacklisted' && z.zone_type !== 'blacklisted') return false;
      if (filter === 'active' && String(z.status) !== '1') return false;
      if (searchTerm && !z.name.toLowerCase().includes(searchTerm.toLowerCase())) return false;
      return true;
    });

    filtered.forEach(zone => {
      if (!zone.polygon || zone.polygon.length < 3) return;
      const isActive = String(zone.status) === '1';
      const color = zone.zone_type === 'blacklisted' ? theme.danger : theme.success;

      const poly = L.polygon(
        zone.polygon.map(p => [p.lat, p.lng]),
        {
          fillColor: color,
          fillOpacity: mapStyle === 'street' ? (isActive ? 0.20 : 0.06) : (isActive ? 0.10 : 0.04),
          color: color,
          weight: mapStyle === 'street' ? (isActive ? 3 : 2) : (isActive ? 4 : 2.5),
          opacity: isActive ? 1 : 0.55,
        }
      ).addTo(mapInstance.current);

      poly.bindPopup(`
        <div style="font-family:system-ui;padding:4px;min-width:140px;">
          <strong>${zone.name}</strong><br/>
          <span style="color:${zone.zone_type === 'blacklisted' ? theme.danger : theme.success};font-size:12px;font-weight:600;">
            ${zone.zone_type === 'blacklisted' ? 'BLACKLISTED' : 'ALLOWED'}
          </span>
          <span style="margin-left:8px;color:${isActive ? theme.success : '#999'};font-size:12px;">
            ${isActive ? 'Active' : 'Inactive'}
          </span>
        </div>
      `);

      poly.on('click', () => focusOnZone(zone.id));
      polygonsRef.current.set(zone.id, poly);
    });
  }, [zones, mapsReady, filter, searchTerm, theme, mapStyle]);

  // ─── Undo / Redo ────────────────────────────────────────────────────
  const pushHistory = (coords) => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(coords);
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  };

  const undo = () => {
    if (historyIndex > 0) {
      const prev = history[historyIndex - 1];
      setDrawnCoords(prev);
      setHistoryIndex(historyIndex - 1);
      updateAreaStats(prev);
      if (editablePoly.current) editablePoly.current.setLatLngs(prev.map(p => [p.lat, p.lng]));
      else if (drawingPoly.current) drawingPoly.current.setLatLngs(prev.map(p => [p.lat, p.lng]));
    }
  };

  const redo = () => {
    if (historyIndex < history.length - 1) {
      const next = history[historyIndex + 1];
      setDrawnCoords(next);
      setHistoryIndex(historyIndex + 1);
      updateAreaStats(next);
      if (editablePoly.current) editablePoly.current.setLatLngs(next.map(p => [p.lat, p.lng]));
      else if (drawingPoly.current) drawingPoly.current.setLatLngs(next.map(p => [p.lat, p.lng]));
    }
  };

  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') { e.preventDefault(); undo(); }
      if ((e.ctrlKey || e.metaKey) && e.key === 'y') { e.preventDefault(); redo(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [history, historyIndex]);

  // ─── Remove Polygon Point Mode ─────────────────────────────────────
  const toggleRemovePointMode = () => {
    const poly = editablePoly.current || drawingPoly.current;
    if (!poly) { showMsg('First draw, select or edit a zone', 'error'); return; }
    const nextMode = !removePointMode;
    setRemovePointMode(nextMode);
    if (nextMode) {
      try { poly.pm?.enable({ allowSelfIntersection: false, snappable: false }); } catch (error) { console.error(error); }
      showMsg('Remove Point ON — right-click a boundary point to remove it');
    } else {
      showMsg('Remove Point mode OFF');
    }
  };

  // ─── Search Area Inside City ───────────────────────────────────────
  const searchAreaInsideCity = async (query) => {
    if (!selectedCity?.name || !query || query.trim().length < 2) return [];
    try {
      setAreaSearching(true);
      const fullQuery = `${query.trim()}, ${selectedCity.name}, India`;
      const response = await getData(`admin/settings/regions/search?q=${encodeURIComponent(fullQuery)}&limit=8&area_only=1`);
      const data = response?.status ? response.data : [];
      return data.map(item => {
        let polygon = null;
        if (item.geojson && item.geojson.type === 'Polygon') {
          const coords = item.geojson.coordinates[0];
          polygon = coords.map(coord => ({ lat: Number(coord[1]), lng: Number(coord[0]) }));
        } else if (item.geojson && item.geojson.type === 'MultiPolygon') {
          const rings = item.geojson.coordinates.map(poly => poly?.[0]).filter(Boolean);
          if (rings.length) {
            const largestRing = rings.reduce((largest, current) => current.length > largest.length ? current : largest);
            polygon = largestRing.map(coord => ({ lat: Number(coord[1]), lng: Number(coord[0]) }));
          }
        }
        return {
          name: item.name || item.display_name?.split(',')[0]?.trim() || query,
          display: item.display_name,
          lat: Number(item.lat),
          lng: Number(item.lon),
          bbox: item.boundingbox || null,
          polygon,
          type: item.type || '',
          category: item.category || '',
          place_id: item.place_id,
          boundary_source: item.boundary_source || null,
          source_link: item.source_link || null,
          matched_place: item.matched_place || null,
          ward_number: item.ward_number || null,
          ward_area_count: item.ward_area_count || null,
          ward_areas: Array.isArray(item.ward_areas) ? item.ward_areas : [],
          matched_areas: Array.isArray(item.matched_areas) ? item.matched_areas : [],
          boundary_scope: item.boundary_scope || null,
          data_source: item.data_source || null,
          source: item.source || 'OpenStreetMap',
        };
      });
    } catch (error) {
      console.error('Area search error:', error);
      return [];
    } finally {
      setAreaSearching(false);
    }
  };

  // ─── Preview Selected Area ─────────────────────────────────────────
  const previewArea = (result, options = {}) => {
    const map = mapInstance.current;
    if (!map || !result) return;
    if (areaPreviewRef.current) { try { areaPreviewRef.current.remove(); } catch {} areaPreviewRef.current = null; }
    if (result.polygon && result.polygon.length >= 3) {
      const preview = L.polygon(
        result.polygon.map(point => [point.lat, point.lng]),
        { color: theme.warning, weight: 4, opacity: 1, fillColor: theme.warning, fillOpacity: 0.12, dashArray: '8,6' }
      ).addTo(map);
      areaPreviewRef.current = preview;
      preview.bringToFront();
      try { map.fitBounds(preview.getBounds(), { padding: [20, 20], maxZoom: 15 }); } catch (_) {}
    } else {
      setMapView(map, [result.lat, result.lng], 14);
    }
    if (!options.keepResults) setShowAreaResults(false);
  };

  const updateAreaStats = (coords) => {
    if (!coords || coords.length < 3) {
      setAreaInfo({ area: null, perimeter: null, points: coords?.length || 0 });
      return;
    }
    try {
      const ring = coords.map(c => [Number(c.lng), Number(c.lat)]);
      const first = ring[0], last = ring[ring.length - 1];
      if (first[0] !== last[0] || first[1] !== last[1]) ring.push([...first]);
      const poly = turfPolygon([ring]);
      const areaSqM = area(poly);
      const boundary = lineString(ring);
      const perimeterKm = length(boundary, { units: 'kilometers' });
      setAreaInfo({ area: areaSqM / 1000000, perimeter: perimeterKm, points: coords.length });
    } catch (error) {
      console.error('Zone area calculation error:', error);
      setAreaInfo({ area: null, perimeter: null, points: coords?.length || 0 });
    }
  };

  const focusOnZone = (id) => {
    const zone = zones.find(z => z.id === id);
    if (!zone || !zone.polygon?.length || !mapInstance.current) return;
    try {
      const bounds = L.latLngBounds(zone.polygon.map(p => [p.lat, p.lng]));
      mapInstance.current.fitBounds(bounds, { padding: [20, 20], maxZoom: 14 });
    } catch (_) {}
  };

  const setMapView = (map, coords, zoom) => {
    const point = Array.isArray(coords)
      ? { lat: Number(coords[0]), lng: Number(coords[1]) }
      : placeCoordinates(coords);
    if (!map || !point || !Number.isFinite(point.lat) || !Number.isFinite(point.lng)) return;
    map.setView([point.lat, point.lng], Number.isFinite(zoom) ? Number(zoom) : 13);
  };

  const fitMapToBounds = (map, bounds, zoom = 13) => {
    try {
      const latLngs = bounds?.points?.map(p => [p.lat, p.lng]) || (Array.isArray(bounds) ? bounds.map(p => Array.isArray(p) ? p : [p.lat, p.lng]) : []);
      if (latLngs.length < 2) return;
      map.fitBounds(latLngs, { padding: [20, 20], maxZoom: zoom });
    } catch (_) {}
  };

  const showMsg = (text, type = 'success') => {
    setMsg({ text, type });
    setTimeout(() => setMsg(null), 3000);
  };

  const handleCityTyping = () => {
    // If this input event was fired from programmatic city selection, skip it
    if (skipCityInputHandler.current) {
      skipCityInputHandler.current = false;
      return;
    }
    const typedCity = citySearchInputRef.current?.value?.trim().toLowerCase() || '';
    citySearchQueryRef.current = typedCity;
    const activeCity = String(selectedCity?.name || '').trim().toLowerCase();
    if (activeCity && typedCity !== activeCity) {
      setSelectedCity(null);
      setAreaQuery('');
      setAreaResults([]);
      setShowAreaResults(false);
      areaSearchBoundRef.current = false;
    }
  };

  const selectCity = (result) => {
    if (!result || !mapInstance.current) return;
    const map = mapInstance.current;
    const lat = Number(result.lat);
    const lng = Number(result.lng ?? result.lon);

    console.log("Selected city:", result.name, lat, lng);

    if (!Number.isFinite(lat) || !Number.isFinite(lng) || lat < 6 || lat > 37 || lng < 68 || lng > 98) {
      showMsg('Invalid coordinates for this city.', 'error');
      return;
    }

    // Signal that this input change is from programmatic selection, not typing
    skipCityInputHandler.current = true;
    // Clear old city so handleCityTyping doesn't also clear things
    setSelectedCity(null);
    setAreaQuery('');
    setAreaResults([]);
    setShowAreaResults(false);
    areaSearchBoundRef.current = false;

    // Remove old boundary
    if (cityBoundaryRef.current) {
      try { cityBoundaryRef.current.remove(); } catch (e) {}
      cityBoundaryRef.current = null;
    }

    // Draw boundary polygon if available
    let boundaryPolygon = null;
    if (result.polygon && Array.isArray(result.polygon) && result.polygon.length >= 3) {
      boundaryPolygon = result.polygon;
    } else if (result.geojson && (result.geojson.type === 'Polygon' || result.geojson.type === 'MultiPolygon')) {
      try {
        const coords = result.geojson.type === 'Polygon' ? result.geojson.coordinates[0] : result.geojson.coordinates[0][0];
        boundaryPolygon = coords.map(coord => ({ lat: Number(coord[1]), lng: Number(coord[0]) }));
      } catch (e) { console.warn('Failed to extract polygon from geojson:', e); }
    }

    if (boundaryPolygon && boundaryPolygon.length >= 3) {
      const boundaryLayer = L.polygon(
        boundaryPolygon.map(p => [p.lat, p.lng]),
        { color: theme.primary, weight: 2, opacity: 0.8, fillColor: theme.primary, fillOpacity: 0.08, dashArray: '6,4', interactive: false }
      ).addTo(map);
      cityBoundaryRef.current = boundaryLayer;
      try { map.fitBounds(boundaryLayer.getBounds(), { padding: [20, 20], maxZoom: 14 }); }
      catch (e) { setMapView(map, [lat, lng], 12); }
      showMsg(`${result.name} - City boundary loaded (${boundaryPolygon.length} points)`, 'success');
    } else {
      setMapView(map, [lat, lng], 13);
      showMsg(`${result.name} selected (point only - no boundary available)`, 'warning');
    }

    const cityData = {
      name: result.name,
      displayName: result.displayName || result.display || result.name,
      lat, lng,
      bbox: result.bbox || null,
      polygon: boundaryPolygon,
      source: result.source || 'OpenStreetMap',
      _searchResults: [],
      _searchQuery: '',
    };

    areaSearchBoundRef.current = false;
    setSelectedCity(cityData);
    if (citySearchInputRef.current) citySearchInputRef.current.value = result.name || "";
    citySearchQueryRef.current = '';
    setAreaQuery('');
    setAreaResults([]);
  };

  // Debounced city search using backend Nominatim
  useEffect(() => {
    if (!mapsReady || citySearchBoundRef.current || !citySearchInputRef.current) return;
    citySearchBoundRef.current = true;

    const input = citySearchInputRef.current;

    const handleInput = async () => {
      // If this was triggered by programmatic city selection, ignore it
      if (skipCityInputHandler.current) {
        skipCityInputHandler.current = false;
        return;
      }
      if (cityDebounceRef.current) clearTimeout(cityDebounceRef.current);
      const query = input.value?.trim() || '';
      if (query.length < 2) return;

      cityDebounceRef.current = setTimeout(async () => {
        const results = await searchLocation(query);
        // Show results in a simple dropdown below the input
        // For now, auto-select the first result if only one
        if (results.length === 1) {
          // Don't auto-select, let user pick
        }
        // Store results for dropdown rendering
        window.__citySearchResults = results;
        window.__citySearchQuery = query;
        // Force re-render by updating state (guard against null)
        setSelectedCity(prev => {
          const base = prev || {};
          return { ...base, _searchResults: results, _searchQuery: query };
        });
      }, 500);
    };

    input.addEventListener('input', handleInput);
    return () => {
      if (cityDebounceRef.current) clearTimeout(cityDebounceRef.current);
      input.removeEventListener('input', handleInput);
      citySearchBoundRef.current = false;
    };
  }, [mapsReady]);

  // Handle city search result selection
  const handleCitySearchSelect = (result) => {
    selectCity(result);
  };

  // Area search inside selected city
  useEffect(() => {
    if (!mapsReady || !selectedCity || areaSearchBoundRef.current || !areaSearchInputRef.current) return;
    areaSearchBoundRef.current = true;
    const input = areaSearchInputRef.current;
    let debounceTimer;

    const handleInput = async () => {
      clearTimeout(debounceTimer);
      const value = input.value?.trim() || '';
      setAreaQuery(value);
      setAreaResults([]);
      setShowAreaResults(false);

      if (value.length >= 2 && selectedCity) {
        debounceTimer = setTimeout(async () => {
          const reqId = ++areaSearchRequestId.current;
          setAreaSearching(true);
          const results = await searchAreaInsideCity(value);
          setAreaSearching(false);
          if (reqId !== areaSearchRequestId.current) return;
          if (results.length) {
            setAreaResults(results);
            setShowAreaResults(true);
          }
        }, 700);
      }
    };

    input.addEventListener('input', handleInput);
    return () => {
      clearTimeout(debounceTimer);
      input.removeEventListener('input', handleInput);
      areaSearchBoundRef.current = false;
    };
  }, [mapsReady, selectedCity]);

  const cancelDraw = () => {
    setMode('view');
    setEditId(null);
    setFormName('');
    setFormType('allowed');
    setBlockingAreaName('');
    setDrawnCoords([]);
    setAreaInfo({ area: null, perimeter: null, points: 0 });
    setHistory([]);
    setHistoryIndex(-1);
    if (manualDrawListener.current) {
      try { mapInstance.current?.off?.('click', manualDrawListener.current); } catch {}
      manualDrawListener.current = null;
    }
    if (mapInstance.current?.pm) mapInstance.current.pm.disableDraw();
    if (drawingPoly.current) { try { drawingPoly.current.remove(); } catch {} drawingPoly.current = null; }
    if (editablePoly.current) { try { editablePoly.current.remove(); } catch {} editablePoly.current = null; }
    setZones([...zones]);
  };

  const startAdd = () => {
    if (!mapInstance.current) return;
    cancelDraw();
    setMode('add');
    const map = mapInstance.current;
    let points = [];
    const onMapClick = async (event) => {
      const point = mapEventPoint(event);
      if (!point) return;
      points = [...points, point];
      if (drawingPoly.current) { try { drawingPoly.current.remove(); } catch {} }
      drawingPoly.current = L.polygon(points, {
        fillColor: theme.primary, fillOpacity: 0.3, color: theme.primary, weight: 3,
      }).addTo(map);
      setDrawnCoords(points);
      updateAreaStats(points);
      if (points.length >= 3) {
        pushHistory(points);
        const centroid = getPolygonCentroid(points);
        if (centroid && !formName) {
          const address = await getAddressFromCoords(centroid.lat, centroid.lng);
          setFormName(address);
        }
        showMsg('Boundary ready. Add more points if needed, then enter a name and Save.', 'success');
      } else {
        showMsg(`Point ${points.length} added — add at least ${3 - points.length} more point(s).`);
      }
    };
    manualDrawListener.current = onMapClick;
    map.on('click', onMapClick);
    showMsg('OpenStreetMap polygon drawing ON — click boundary points, then press Save.');
  };

  const startFreehand = () => {
    startAdd();
    showMsg('OpenStreetMap mode: use Polygon clicks to draw the exact boundary.');
  };

  const startRadius = () => {
    if (!mapInstance.current) return;
    const radiusKm = parseFloat(window.prompt('Enter radius in kilometers:', '2'));
    if (isNaN(radiusKm) || radiusKm <= 0) return;
    cancelDraw();
    setMode('add');
    const map = mapInstance.current;
    const onMapClick = async (e) => {
      const center = mapEventPoint(e);
      if (!center) return;
      const points = circleToPolygonPoints(center, radiusKm);
      const poly = L.polygon(points.map(p => [p.lat, p.lng]), {
        fillColor: theme.primary, fillOpacity: 0.3, color: theme.primary, weight: 2,
      }).addTo(map);
      drawingPoly.current = poly;
      setDrawnCoords(points);
      updateAreaStats(points);
      pushHistory(points);
      const centroid = getPolygonCentroid(points);
      if (centroid) {
        const address = await getAddressFromCoords(centroid.lat, centroid.lng);
        setFormName(address);
      }
      poly.pm?.enable?.();
      poly.on('pm:edit', () => {
        const newCoords = polyToCoords(poly);
        setDrawnCoords(newCoords);
        updateAreaStats(newCoords);
        pushHistory(newCoords);
      });
      map.off('click', onMapClick);
    };
    map.on('click', onMapClick);
  };

  const startEdit = (zone) => {
    if (!mapInstance.current) return;
    cancelDraw();
    setMode('edit');
    setEditId(zone.id);
    setFormName(zone.name);
    setFormType(zone.zone_type || 'allowed');
    const coords = zone.polygon || [];
    setDrawnCoords(coords);
    updateAreaStats(coords);
    setHistory([coords]);
    setHistoryIndex(0);
    const orig = polygonsRef.current.get(zone.id);
    if (orig) orig.remove();
    const poly = L.polygon(coords.map(p => [p.lat, p.lng]), {
      fillColor: zone.zone_type === 'blacklisted' ? theme.danger : theme.success,
      fillOpacity: 0.35,
      color: zone.zone_type === 'blacklisted' ? theme.danger : theme.success,
      weight: 3,
    }).addTo(mapInstance.current);
    editablePoly.current = poly;
    poly.pm?.enable?.();
    poly.on('pm:edit', () => {
      const newCoords = polyToCoords(poly);
      setDrawnCoords(newCoords);
      updateAreaStats(newCoords);
      pushHistory(newCoords);
    });
    try { mapInstance.current.fitBounds(poly.getBounds(), { padding: [20, 20], maxZoom: 15 }); } catch (_) {}
  };

  const addAreaToBatch = (result) => {
    if (result.boundary_scope === 'ward_reference') { drawBlockedArea(result); return; }
    const polygon = result.polygon;
    if (!polygon || polygon.length < 3) {
      showMsg(`"${result.name}" has no boundary data. Try manual drawing.`, 'error');
      return;
    }
    if (selectedAreas.some(a => a.place_id === result.place_id)) {
      showMsg('Area already selected!', 'warning');
      return;
    }
    const newArea = { ...result, polygon, type: 'pending' };
    setSelectedAreas(prev => [...prev, newArea]);
    if (mapInstance.current) {
      const poly = L.polygon(
        polygon.map(p => [p.lat, p.lng]),
        { color: '#8b5cf6', weight: 3, opacity: 0.8, fillColor: '#8b5cf6', fillOpacity: 0.1, dashArray: '5,5' }
      ).addTo(mapInstance.current);
      selectedAreaLayers.current.set(result.place_id || Date.now(), poly);
    }
    setShowAreaResults(false);
    setAreaQuery('');
    showMsg(`"${result.name}" added. Now choose Allow or Blacklist.`);
  };

  const updateSelectedAreaType = async (index, type) => {
    const area = selectedAreas[index];
    if (!area) return;
    const updatedAreas = [...selectedAreas];
    updatedAreas[index].type = type;
    setSelectedAreas(updatedAreas);
    const layer = selectedAreaLayers.current.get(area.place_id);
    if (layer) {
      const color = type === 'allowed' ? theme.success : theme.danger;
      layer.setStyle({ color, fillColor: color, fillOpacity: 0.25, dashArray: null });
      layer.bringToFront();
    }
    setSaving(true);
    try {
      const res = await createZone({ name: area.name.trim(), city: selectedCity?.name || 'Gwalior', polygon: area.polygon, zone_type: type, status: '1' });
      if (res.status) {
        showMsg(`"${area.name}" marked as ${type.toUpperCase()} and saved!`);
        await loadZones();
      } else throw new Error(res.message || 'Failed to save');
    } catch (e) {
      showMsg(e.message || 'Error saving zone', 'error');
      const revertAreas = [...selectedAreas];
      revertAreas[index].type = 'pending';
      setSelectedAreas(revertAreas);
      if (layer) layer.setStyle({ color: '#8b5cf6', fillColor: '#8b5cf6', fillOpacity: 0.1, dashArray: '5,5' });
    } finally {
      setSaving(false);
    }
  };

  const removeSelectedArea = (index) => {
    const area = selectedAreas[index];
    if (!area) return;
    const layer = selectedAreaLayers.current.get(area.place_id);
    if (layer) { try { layer.remove(); } catch (e) {} selectedAreaLayers.current.delete(area.place_id); }
    const updatedAreas = [...selectedAreas];
    updatedAreas.splice(index, 1);
    setSelectedAreas(updatedAreas);
    showMsg(`Removed "${area.name}" from selection`);
  };

  const testServiceability = async (lat, lng, moveMap = false) => {
    if (!Number.isFinite(Number(lat)) || !Number.isFinite(Number(lng))) return;
    setTestingLocation(true);
    try {
      const result = await postData('admin/settings/regions/check', { lat: Number(lat), lng: Number(lng) });
      setTestResult({ ...result, lat: Number(lat), lng: Number(lng) });
      if (mapInstance.current) {
        if (testMarkerRef.current) testMarkerRef.current.remove();
        testMarkerRef.current = L.circleMarker([Number(lat), Number(lng)], {
          radius: 8, color: result.allowed ? theme.success : theme.danger,
          fillColor: result.allowed ? theme.success : theme.danger, fillOpacity: 0.9, weight: 3,
        }).addTo(mapInstance.current);
        testMarkerRef.current.bindPopup(`<strong>${result.allowed ? 'Delivery available' : 'Delivery unavailable'}</strong><br/>${result.zone?.name || result.message || ''}`).openPopup();
        if (moveMap) setMapView(mapInstance.current, [Number(lat), Number(lng)], 16);
      }
    } catch (error) {
      showMsg(error.message || 'Could not test this location', 'error');
    } finally {
      setTestingLocation(false);
    }
  };

  const drawBlockedArea = (result) => {
    selectedAreaLayers.current.forEach(layer => { try { layer.remove(); } catch {} });
    selectedAreaLayers.current.clear();
    setSelectedAreas([]);
    previewArea(result);
    startAdd();
    setFormType('blacklisted');
    setBlockingAreaName(result.name);
    setFormName(`Blocked area - ${result.name}`);
    showMsg(`BLOCK ${result.name}: click boundary points on map, then Save Blocked Area.`);
  };

  const testMapCenter = () => {
    const center = mapInstance.current?.getCenter();
    if (center) testServiceability(center.lat, center.lng);
  };

  const locateAndTest = () => {
    if (!navigator.geolocation) { showMsg('Location is not supported by this browser', 'error'); return; }
    setTestingLocation(true);
    navigator.geolocation.getCurrentPosition(
      pos => testServiceability(pos.coords.latitude, pos.coords.longitude, true),
      err => { setTestingLocation(false); showMsg(err.message || 'Could not access your location', 'error'); },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 15000 }
    );
  };

  const handleSave = async () => {
    if (!formName.trim()) { showMsg('Zone name is required', 'error'); return; }
    if (drawnCoords.length < 3) { showMsg('Draw a polygon first (at least 3 points)', 'error'); return; }
    setSaving(true);
    try {
      if (mode === 'add') {
        const res = await createZone({ name: formName.trim(), city: selectedCity?.name || 'Gwalior', polygon: drawnCoords, zone_type: blockingAreaName ? 'blacklisted' : formType, status: '1' });
        if (res.status) { showMsg('Zone created successfully'); await loadZones(); cancelDraw(); }
        else throw new Error(res.message || 'Failed to create');
      } else if (mode === 'edit' && editId) {
        const res = await updateZone(editId, { name: formName.trim(), city: selectedCity?.name || 'Gwalior', polygon: drawnCoords, zone_type: formType });
        if (res.status) { showMsg('Zone updated'); await loadZones(); cancelDraw(); }
        else throw new Error(res.message || 'Failed to update');
      }
    } catch (e) { showMsg(e.message || 'Error saving', 'error'); }
    finally { setSaving(false); }
  };

  const toggleStatus = async (zone) => {
    const newStatus = String(zone.status) === '1' ? '0' : '1';
    const res = await updateZone(zone.id, { status: newStatus });
    if (res.status) { showMsg(`Zone ${newStatus === '1' ? 'activated' : 'deactivated'}`); await loadZones(); }
    else showMsg(res.message || 'Error', 'error');
  };

  const toggleType = async (zone) => {
    const newType = zone.zone_type === 'blacklisted' ? 'allowed' : 'blacklisted';
    const res = await updateZone(zone.id, { zone_type: newType });
    if (res.status) { showMsg(`Zone marked as ${newType}`); await loadZones(); }
    else showMsg(res.message || 'Error', 'error');
  };

  const handleDelete = async (zone) => {
    if (!window.confirm(`Delete "${zone.name}"? This cannot be undone.`)) return;
    const res = await deleteZoneApi(zone.id);
    if (res.status) { showMsg('Zone deleted'); await loadZones(); }
    else showMsg(res.message || 'Error', 'error');
  };

  const filteredZones = useMemo(() => {
    let list = zones;
    if (filter === 'allowed') list = list.filter(z => z.zone_type === 'allowed');
    else if (filter === 'blacklisted') list = list.filter(z => z.zone_type === 'blacklisted');
    else if (filter === 'active') list = list.filter(z => String(z.status) === '1');
    if (searchTerm) list = list.filter(z => z.name.toLowerCase().includes(searchTerm.toLowerCase()));
    return list;
  }, [zones, filter, searchTerm]);

  const stats = useMemo(() => ({
    total: zones.length,
    allowed: zones.filter(z => z.zone_type === 'allowed').length,
    blacklisted: zones.filter(z => z.zone_type === 'blacklisted').length,
    active: zones.filter(z => String(z.status) === '1').length,
  }), [zones]);

  const SpinnerInline = () => (
    <div className="spinner" style={{ border: '4px solid rgba(0,0,0,0.1)', borderRadius: '50%', borderTop: `4px solid ${theme.primary}`, width: '24px', height: '24px', animation: 'spin 0.8s linear infinite', margin: '0 auto' }} />
  );

  if (loading && !mapsReady && !mapLoadError) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: theme.bg }}>
        <SpinnerInline />
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // City search results dropdown
  const citySearchResults = (selectedCity && selectedCity._searchResults) || window.__citySearchResults || [];
  const citySearchQuery = (selectedCity && selectedCity._searchQuery) || window.__citySearchQuery || '';

  return (
    <div style={{ height: '100vh', background: theme.bg, padding: '20px 24px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Inter", sans-serif', color: theme.text, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <style>{`
        * { margin: 0; padding: 0; box-sizing: border-box; }
        button { transition: all 0.2s ease; cursor: pointer; }
        button:hover { opacity: 0.85; }
        button:active { transform: scale(0.98); }
        input:focus { outline: none; border-color: ${theme.primary} !important; }
        ::-webkit-scrollbar { width: 5px; height: 5px; }
        ::-webkit-scrollbar-track { background: ${theme.border}; border-radius: 3px; }
        ::-webkit-scrollbar-thumb { background: ${theme.primary}; border-radius: 3px; }
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        .leaflet-container { background: #e8e8e8 !important; }
      `}</style>

      {/* Header */}
      <div style={{ marginBottom: '20px', flexShrink: 0 }}>
        <h1 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '6px', letterSpacing: '-0.3px' }}>Zone Management</h1>
        <p style={{ fontSize: '13px', color: theme.textSecondary }}>Create and manage delivery zones using OpenStreetMap (free)</p>
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', gap: '14px', marginBottom: '20px', flexWrap: 'wrap', flexShrink: 0 }}>
        {[
          { label: 'Total Zones', value: stats.total, color: theme.primary },
          { label: 'Allowed Zones', value: stats.allowed, color: theme.success },
          { label: 'Blacklisted Zones', value: stats.blacklisted, color: theme.danger },
          { label: 'Active Zones', value: stats.active, color: theme.warning },
        ].map(s => (
          <div key={s.label} style={{ background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: '10px', padding: '14px 18px', flex: 1, minWidth: '100px' }}>
            <div style={{ fontSize: '11px', color: theme.textSecondary, marginBottom: '6px', letterSpacing: '0.3px' }}>{s.label}</div>
            <div style={{ fontSize: '28px', fontWeight: 700, color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      {msg && (
        <div style={{ padding: '10px 16px', borderRadius: '8px', marginBottom: '14px', background: msg.type === 'error' ? theme.dangerLight : theme.successLight, color: msg.type === 'error' ? theme.danger : theme.success, fontSize: '13px', fontWeight: 500 }}>
          {msg.text}
        </div>
      )}

      {/* Main Layout */}
      <div style={{ display: 'flex', gap: '20px', flex: 1, minHeight: 0, overflow: 'hidden' }}>
        {/* Left Panel: Map */}
        <div style={{ flex: 1, minWidth: '300px', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: '12px', overflow: 'hidden', display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Toolbar & Search */}
            <div style={{ padding: '10px 14px', borderBottom: `1px solid ${theme.border}`, flexShrink: 0 }}>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
                {/* City Search */}
                <div style={{ position: 'relative', flex: 1, minWidth: '150px' }}>
                  <input
                    ref={citySearchInputRef}
                    type="text"
                    placeholder="Search any Indian city (OpenStreetMap / Nominatim)..."
                    onInput={handleCityTyping}
                    style={{ width: '100%', padding: '7px 10px', background: theme.bg, border: `1px solid ${theme.border}`, borderRadius: '6px', fontSize: '12px', color: theme.text }}
                  />
                  {/* City search results dropdown */}
                  {citySearchResults.length > 0 && citySearchQuery.length >= 2 && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: '4px', background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: '7px', boxShadow: '0 5px 18px rgba(0,0,0,0.18)', maxHeight: '200px', overflowY: 'auto', zIndex: 5000 }}>
                      {citySearchResults.map((result, index) => (
                        <div key={result.place_id || index} onClick={() => handleCitySearchSelect(result)} style={{ padding: '8px 10px', borderBottom: `1px solid ${theme.border}`, cursor: 'pointer', fontSize: '12px' }} onMouseEnter={(e) => e.currentTarget.style.background = theme.primary + '15'} onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                          <strong style={{ color: theme.text }}>{result.name}</strong>
                          <div style={{ fontSize: '10px', color: theme.textMuted, marginTop: '2px' }}>{result.display}</div>
                          {result.polygon?.length >= 3 && <span style={{ fontSize: '9px', color: theme.success, fontWeight: 600 }}>📐 polygon ({result.polygon.length} pts)</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Map style switcher */}
                <div style={{ display: 'flex', gap: '3px', padding: '3px', background: theme.bg, border: `1px solid ${theme.border}`, borderRadius: '7px' }}>
                  {[
                    { key: 'street', icon: '🗺️', label: 'Street' },
                    { key: 'satellite', icon: '🛰️', label: 'Satellite' },
                  ].map(option => {
                    const active = mapStyle === option.key;
                    return (
                      <button key={option.key} type="button" onClick={() => setMapStyle(option.key)} style={{ padding: '5px 10px', border: 'none', background: active ? theme.primary : 'transparent', color: active ? '#fff' : theme.text, borderRadius: '5px', fontSize: '10px', fontWeight: 700 }}>
                        {option.icon} {option.label}
                      </button>
                    );
                  })}
                </div>

                <button onClick={toggleRemovePointMode} style={{ padding: '6px 12px', background: removePointMode ? theme.danger : theme.bg, color: removePointMode ? '#fff' : theme.textSecondary, border: `1px solid ${removePointMode ? theme.danger : theme.border}`, borderRadius: '6px', fontSize: '11px', fontWeight: 600 }}>Remove Point</button>
                <button onClick={startAdd} style={{ padding: '6px 14px', background: theme.primary, color: '#fff', border: 'none', borderRadius: '6px', fontSize: '11px', fontWeight: 600 }}>Polygon</button>
                <button onClick={startFreehand} style={{ padding: '6px 14px', background: theme.primary, color: '#fff', border: 'none', borderRadius: '6px', fontSize: '11px', fontWeight: 600 }}>Brush</button>
                <button onClick={startRadius} style={{ padding: '6px 14px', background: theme.primary, color: '#fff', border: 'none', borderRadius: '6px', fontSize: '11px', fontWeight: 600 }}>Radius</button>
                <button onClick={testMapCenter} disabled={testingLocation} style={{ padding: '6px 12px', background: theme.bg, color: theme.textSecondary, border: `1px solid ${theme.border}`, borderRadius: '6px', fontSize: '11px', fontWeight: 600 }}>{testingLocation ? 'Testing...' : 'Test Center'}</button>
                <button onClick={locateAndTest} disabled={testingLocation} style={{ padding: '6px 12px', background: theme.bg, color: theme.textSecondary, border: `1px solid ${theme.border}`, borderRadius: '6px', fontSize: '11px', fontWeight: 600 }}>My Location</button>
                <button onClick={undo} disabled={historyIndex <= 0} style={{ padding: '6px 12px', background: theme.bg, color: theme.text, border: `1px solid ${theme.border}`, borderRadius: '6px', fontSize: '11px', opacity: historyIndex > 0 ? 1 : 0.5 }}>Undo</button>
                <button onClick={redo} disabled={historyIndex >= history.length - 1} style={{ padding: '6px 12px', background: theme.bg, color: theme.text, border: `1px solid ${theme.border}`, borderRadius: '6px', fontSize: '11px', opacity: historyIndex < history.length - 1 ? 1 : 0.5 }}>Redo</button>
                {mode !== 'view' && (
                  <button onClick={cancelDraw} style={{ padding: '6px 12px', background: theme.danger, color: '#fff', border: 'none', borderRadius: '6px', fontSize: '11px', fontWeight: 500 }}>Cancel</button>
                )}
              </div>

              {/* Drawing form */}
              {mode !== 'view' && (
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', marginTop: '6px' }}>
                  {blockingAreaName && <div style={{ width: '100%', padding: '7px 10px', background: theme.dangerLight, color: theme.danger, border: `1px solid ${theme.danger}`, borderRadius: '6px', fontSize: '11px', fontWeight: 700 }}>Blocking {blockingAreaName}: click boundary points on map, then Save.</div>}
                  <input value={formName} onChange={e => setFormName(e.target.value)} placeholder="Zone name..." style={{ flex: 1, padding: '6px 10px', background: theme.bg, border: `1px solid ${theme.border}`, borderRadius: '6px', fontSize: '12px', color: theme.text, minWidth: '120px' }} />
                  <div style={{ display: 'flex', gap: '4px', background: theme.bg, borderRadius: '6px', border: `1px solid ${theme.border}`, padding: '2px' }}>
                    <button disabled={Boolean(blockingAreaName)} onClick={() => setFormType('allowed')} style={{ padding: '4px 10px', borderRadius: '4px', fontSize: '11px', fontWeight: 500, background: formType === 'allowed' ? theme.success : 'transparent', color: formType === 'allowed' ? '#fff' : theme.textSecondary, border: 'none', opacity: blockingAreaName ? 0.35 : 1 }}>Allowed</button>
                    <button onClick={() => setFormType('blacklisted')} style={{ padding: '4px 10px', borderRadius: '4px', fontSize: '11px', fontWeight: 500, background: formType === 'blacklisted' ? theme.danger : 'transparent', color: formType === 'blacklisted' ? '#fff' : theme.textSecondary, border: 'none' }}>Blacklisted</button>
                  </div>
                  <button onClick={handleSave} disabled={saving} style={{ padding: '6px 16px', background: theme.primary, color: '#fff', border: 'none', borderRadius: '6px', fontSize: '11px', fontWeight: 600 }}>
                    {saving ? 'Saving...' : blockingAreaName ? 'Save Blocked Area' : 'Save'}
                  </button>
                </div>
              )}
            </div>

            {/* Map container */}
            <div style={{ flex: 1, minHeight: '300px', background: theme.bg, position: 'relative' }}>
              <div id="zaptodoor-regions-map" ref={mapRef} style={{ position: 'absolute', inset: 0, zIndex: 1 }} />
              {!mapsReady && (
                <div style={{ position: 'absolute', inset: 0, zIndex: 5, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '40px', pointerEvents: mapLoadError ? 'auto' : 'none' }}>
                  <SpinnerInline />
                  <p style={{ fontSize: '12px', color: theme.textSecondary, marginTop: '8px' }}>{mapLoadError || 'Loading OpenStreetMap...'}</p>
                  {mapLoadError && (
                    <button type="button" onClick={retryMap} style={{ marginTop: '8px', padding: '7px 12px', background: theme.primary, color: '#fff', border: 'none', borderRadius: '6px', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}>Retry</button>
                  )}
                </div>
              )}
            </div>

            {testResult && (
              <div style={{ padding: '9px 14px', borderTop: `1px solid ${theme.border}`, background: testResult.allowed ? theme.successLight : theme.dangerLight, color: testResult.allowed ? theme.success : theme.danger, fontSize: '12px', fontWeight: 600 }}>
                {testResult.allowed ? 'Delivery available' : 'Delivery unavailable'} at {testResult.lat.toFixed(6)}, {testResult.lng.toFixed(6)}
                {testResult.zone?.name ? ` — ${testResult.zone.name}` : ` — ${testResult.message || ''}`}
              </div>
            )}

            {areaInfo.area !== null && (
              <div style={{ padding: '8px 14px', borderTop: `1px solid ${theme.border}`, background: theme.surface, fontSize: '12px', color: theme.textSecondary, display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                <span>Points: <strong>{areaInfo.points}</strong></span>
                <span>Area: <strong>{areaInfo.area.toFixed(2)} km²</strong></span>
                <span>Perimeter: <strong>{areaInfo.perimeter.toFixed(2)} km</strong></span>
              </div>
            )}
          </div>
        </div>

        {/* Right Panel: Zones List */}
        <div style={{ flex: 1, minWidth: '320px', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: '12px', padding: '16px', display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            <div style={{ display: 'flex', gap: '12px', marginBottom: '14px', flexShrink: 0 }}>
              <input type="text" placeholder="Search zones by name..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} style={{ flex: 1, padding: '9px 12px', background: theme.bg, border: `1px solid ${theme.border}`, borderRadius: '8px', fontSize: '12px', color: theme.text }} />
              <button onClick={startAdd} style={{ padding: '9px 18px', background: theme.primary, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap' }}>+ Add Zone</button>
            </div>

            {/* Search Area Inside Selected City */}
            {selectedCity?.name && (
              <div style={{ position: 'relative', marginTop: '8px', marginBottom: '8px' }}>
                <div style={{ fontSize: '10px', fontWeight: 600, color: theme.textMuted, marginBottom: '5px', textTransform: 'uppercase' }}>
                  Search delivery area inside {selectedCity.name.toUpperCase()}
                </div>
                <input
                  ref={areaSearchInputRef}
                  type="text"
                  value={areaQuery}
                  placeholder={`Search Ward, locality...`}
                  onChange={(e) => {
                    // Only update state here; search logic is handled by the useEffect listener below
                    setAreaQuery(e.target.value);
                  }}
                  style={{ width: '100%', padding: '9px 11px', background: theme.bg, color: theme.text, border: `1px solid ${theme.border}`, borderRadius: '6px', fontSize: '12px' }}
                />

                {/* Selected Areas List */}
                {selectedAreas.length > 0 && (
                  <div style={{ marginTop: '10px', marginBottom: '8px' }}>
                    <div style={{ fontSize: '10px', fontWeight: 600, color: theme.textMuted, marginBottom: '6px', textTransform: 'uppercase' }}>
                      Selected Areas ({selectedAreas.length}) — Click Allow or Blacklist
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {selectedAreas.map((area, idx) => (
                        <div key={area.place_id || idx} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: theme.bg, border: `1px solid ${area.type === 'allowed' ? theme.success : area.type === 'blacklisted' ? theme.danger : '#8b5cf6'}`, borderRadius: '6px', padding: '4px 8px', fontSize: '11px', fontWeight: 500 }}>
                          <span style={{ color: theme.text }}>{area.name}</span>
                          {area.type === 'pending' && <span style={{ fontSize: '9px', color: theme.textMuted }}>(pending)</span>}
                          <button onClick={() => updateSelectedAreaType(idx, 'allowed')} disabled={area.type === 'allowed' || saving} style={{ padding: '2px 8px', background: area.type === 'allowed' ? theme.success : 'transparent', color: area.type === 'allowed' ? '#fff' : theme.success, border: `1px solid ${theme.success}`, borderRadius: '4px', fontSize: '9px', fontWeight: 700, cursor: 'pointer', opacity: area.type === 'allowed' ? 1 : 0.7 }}>Allow</button>
                          <button onClick={() => updateSelectedAreaType(idx, 'blacklisted')} disabled={area.type === 'blacklisted' || saving} style={{ padding: '2px 8px', background: area.type === 'blacklisted' ? theme.danger : 'transparent', color: area.type === 'blacklisted' ? '#fff' : theme.danger, border: `1px solid ${theme.danger}`, borderRadius: '4px', fontSize: '9px', fontWeight: 700, cursor: 'pointer', opacity: area.type === 'blacklisted' ? 1 : 0.7 }}>Blacklist</button>
                          <button onClick={() => removeSelectedArea(idx)} style={{ background: 'transparent', border: 'none', color: theme.textMuted, fontSize: '12px', cursor: 'pointer', padding: '0 2px' }}>X</button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {areaSearching && <div style={{ position: 'absolute', right: '10px', top: '31px', fontSize: '10px', color: theme.textMuted }}>Searching...</div>}

                {showAreaResults && areaResults.length > 0 && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: '4px', background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: '7px', boxShadow: '0 5px 18px rgba(0,0,0,0.18)', maxHeight: '260px', overflowY: 'auto', zIndex: 5000 }}>
                    {areaResults.map((result, index) => (
                      <div key={result.place_id || index} style={{ padding: '9px 10px', borderBottom: `1px solid ${theme.border}` }}>
                        <div style={{ fontSize: '12px', fontWeight: 600, color: theme.text }}>{result.name}</div>
                        <div style={{ fontSize: '9px', color: theme.textMuted, marginTop: '2px' }}>{result.display}</div>
                        {(() => {
                          const km = distanceKm(selectedCity?.name && result, selectedCity);
                          return km === null ? null : <div style={{ fontSize: '9px', color: theme.primary, marginTop: '3px', fontWeight: 700 }}>Distance from {selectedCity?.name}: {km.toFixed(1)} km</div>;
                        })()}
                        {result.boundary_scope === 'complete_ward' && <div style={{ fontSize: '9px', color: theme.primary, marginTop: '3px', fontWeight: 700 }}>Complete Ward {result.ward_number} boundary ({result.ward_area_count || 0} areas)</div>}
                        {result.ward_areas?.length > 0 && (
                          <details style={{ marginTop: '6px', background: theme.bg, border: `1px solid ${theme.border}`, borderRadius: '5px', padding: '5px 7px' }}>
                            <summary style={{ cursor: 'pointer', fontSize: '10px', fontWeight: 700, color: theme.primary }}>
                              View all {result.ward_areas.length} areas in Ward {result.ward_number}
                            </summary>
                            <div style={{ marginTop: '6px', maxHeight: '130px', overflowY: 'auto', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                              {result.ward_areas.map((areaName, areaIndex) => (
                                <button key={`${result.ward_number}-${areaIndex}`} type="button" onMouseDown={(e) => { e.preventDefault(); drawBlockedArea({ ...result, name: areaName, matched_place: areaName, boundary_scope: 'ward_reference' }); setShowAreaResults(false); }} style={{ padding: '4px 7px', background: result.matched_areas?.includes(areaName) ? theme.dangerLight : theme.surface, color: result.matched_areas?.includes(areaName) ? theme.danger : theme.textSecondary, border: `1px solid ${result.matched_areas?.includes(areaName) ? theme.danger : theme.border}`, borderRadius: '4px', fontSize: '9px', cursor: 'pointer', textAlign: 'left' }}>
                                  Block {areaName}
                                </button>
                              ))}
                            </div>
                          </details>
                        )}
                        {result.boundary_source && <div style={{ fontSize: '9px', color: theme.success, marginTop: '3px', fontWeight: 700 }}>Verified polygon: {result.boundary_source}</div>}
                        <div style={{ display: 'flex', gap: '6px', marginTop: '6px', flexWrap: 'wrap' }}>
                          <button onMouseDown={(e) => { e.preventDefault(); previewArea(result); }} style={{ padding: '4px 9px', background: theme.warning, color: '#fff', border: 'none', borderRadius: '4px', fontSize: '10px', fontWeight: 600, cursor: 'pointer' }}>Preview</button>
                          <button onMouseDown={(e) => { e.preventDefault(); addAreaToBatch(result); setShowAreaResults(false); }} style={{ padding: '4px 12px', background: '#8b5cf6', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '10px', fontWeight: 700, cursor: 'pointer' }}>Select</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div style={{ display: 'flex', gap: '8px', borderBottom: `1px solid ${theme.border}`, paddingBottom: '12px', flexWrap: 'wrap', flexShrink: 0 }}>
              {[
                { id: 'all', label: 'All Zones', count: stats.total },
                { id: 'allowed', label: 'Allowed', count: stats.allowed },
                { id: 'blacklisted', label: 'Blacklisted', count: stats.blacklisted },
                { id: 'active', label: 'Active Only', count: stats.active },
              ].map(tab => (
                <button key={tab.id} onClick={() => setFilter(tab.id)} style={{ padding: '7px 16px', background: filter === tab.id ? theme.primary : 'transparent', color: filter === tab.id ? '#fff' : theme.textSecondary, border: filter === tab.id ? 'none' : `1px solid ${theme.border}`, borderRadius: '8px', fontSize: '12px', fontWeight: 500 }}>
                  {tab.label} <span style={{ fontSize: '11px', opacity: 0.7 }}>({tab.count})</span>
                </button>
              ))}
            </div>

            <div style={{ flex: 1, overflowY: 'auto', marginTop: '14px', paddingRight: '4px' }}>
              {loading ? (
                <div style={{ textAlign: 'center', padding: '40px' }}><SpinnerInline /></div>
              ) : filteredZones.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '48px 20px', color: theme.textSecondary }}>
                  <p style={{ fontSize: '13px', marginBottom: '4px' }}>No zones found</p>
                  <p style={{ fontSize: '11px' }}>Adjust filters or add a new zone</p>
                </div>
              ) : (
                <>
                  <div style={{ fontSize: '11px', fontWeight: 600, color: theme.textSecondary, marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {filteredZones.length} Zone{filteredZones.length !== 1 ? 's' : ''}
                  </div>
                  {filteredZones.map(zone => (
                    <div key={zone.id} style={{ background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: '10px', padding: '12px 14px', marginBottom: '10px', cursor: 'pointer', borderLeft: `3px solid ${zone.zone_type === 'allowed' ? theme.success : theme.danger}` }} onClick={() => focusOnZone(zone.id)}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                        <div>
                          <h3 style={{ fontSize: '14px', fontWeight: 600, color: theme.text, margin: 0, marginBottom: '4px' }}>{zone.name}</h3>
                          <div style={{ fontSize: '11px', color: theme.textSecondary }}>{zone.polygon?.length || 0} boundary points</div>
                        </div>
                        <button onClick={(e) => { e.stopPropagation(); handleDelete(zone); }} style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '12px', color: theme.textMuted, padding: '4px 8px', borderRadius: '4px' }}>Delete</button>
                      </div>
                      <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
                        <span style={{ padding: '4px 10px', borderRadius: '6px', fontSize: '10px', fontWeight: 600, background: zone.zone_type === 'allowed' ? theme.successLight : theme.dangerLight, color: zone.zone_type === 'allowed' ? theme.success : theme.danger }}>
                          {zone.zone_type === 'allowed' ? 'Allowed' : 'Blacklisted'}
                        </span>
                        <span style={{ padding: '4px 10px', borderRadius: '6px', fontSize: '10px', fontWeight: 600, background: String(zone.status) === '1' ? theme.successLight : theme.warningLight, color: String(zone.status) === '1' ? theme.success : theme.warning }}>
                          {String(zone.status) === '1' ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <div style={{ display: 'flex', gap: '8px', borderTop: `1px solid ${theme.border}`, paddingTop: '10px', marginTop: '2px' }}>
                        <button onClick={(e) => { e.stopPropagation(); toggleStatus(zone); }} style={{ flex: 1, padding: '6px 12px', background: theme.primaryLight, color: theme.primary, border: 'none', borderRadius: '6px', fontSize: '11px', fontWeight: 500 }}>
                          {String(zone.status) === '1' ? 'Deactivate' : 'Activate'}
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); toggleType(zone); }} style={{ flex: 1, padding: '6px 12px', background: theme.primaryLight, color: theme.primary, border: 'none', borderRadius: '6px', fontSize: '11px', fontWeight: 500 }}>Toggle Type</button>
                        <button onClick={(e) => { e.stopPropagation(); startEdit(zone); }} style={{ flex: 1, padding: '6px 12px', background: theme.primaryLight, color: theme.primary, border: 'none', borderRadius: '6px', fontSize: '11px', fontWeight: 500 }}>Edit</button>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
